import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import BigNumber from "bignumber.js";
import { Box, Button, Card, Link, Skeleton, Typography } from "@mui/material";

// components
import PairLogos from "../../components/PairLogos";
import TokenLogo from "../../components/TokenLogo";
import StatsRow from "../../components/StatsRow";
import StatusPage from "../../components/StatusPage";

// data
import { useExplorePool } from "../../hooks/explore";
import { amountType } from "../../theme/main";
import { formatAmount, formatCount, formatUSD } from "../../utils/format";
import { formatVolume, poolName, tokenHref } from "./columns";
import { AddressRow, DetailError, ExploreCrumbs, Section, useStartSwap } from "./detail";

// "1 KOIN = 80.84 KOINDX" from the reserves
const rate = (from, to, reserveFrom, reserveTo) => {
  const value = new BigNumber(reserveTo).dividedBy(reserveFrom);
  return value.isFinite() && value.isGreaterThan(0)
    ? `1 ${from.symbol} = ${formatAmount(value.toFixed(), { maxDecimals: value.isLessThan(1) ? 8 : 4 })} ${to.symbol}`
    : null;
};

const PooledToken = ({ token, amount }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1.5 }}>
    <TokenLogo token={token} size={32} />
    <Link
      component={RouterLink}
      to={tokenHref(token)}
      underline="hover"
      color="inherit"
      sx={{ fontWeight: 600, flexGrow: 1, minWidth: 0 }}
      noWrap
    >
      {token.symbol}
    </Link>
    <Typography sx={{ textAlign: "right" }}>{formatAmount(amount, { maxDecimals: 4 })}</Typography>
  </Box>
);

const PoolPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const pool = useExplorePool(id);
  const { canSwap, startSwap } = useStartSwap();
  const data = pool.data;

  if (pool.notFound) {
    return (
      <StatusPage
        title="Pool not found"
        actions={
          <Button variant="contained" onClick={() => navigate("/explore/pools")}>
            Back to pools
          </Button>
        }
      >
        There is no KoinDX pool at this address.
      </StatusPage>
    );
  }

  const rates = data
    ? [
        rate(data.token0, data.token1, data.reserve0, data.reserve1),
        rate(data.token1, data.token0, data.reserve1, data.reserve0),
      ].filter(Boolean)
    : [];

  return (
    <Box sx={{ maxWidth: 960, mx: "auto", pb: { xs: 10, md: 6 } }}>
      <ExploreCrumbs section="Pools" href="/explore/pools" current={data ? poolName(data) : "…"} />
      {pool.isError ? <DetailError what="pool" onRetry={pool.refetch} /> : null}

      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2, mb: 3 }}>
        {data ? (
          <PairLogos tokens={[data.token0, data.token1]} size={44} />
        ) : (
          <Skeleton variant="circular" width={44} height={44} />
        )}
        <Typography variant="h4" component="h1" noWrap sx={{ flexGrow: 1, minWidth: 0 }}>
          {data ? poolName(data) : <Skeleton width={220} />}
        </Typography>
        {data ? (
          <Box sx={{ display: "flex", gap: 1 }}>
            {canSwap(data.token0.id, data.token1.id) ? (
              <Button variant="outlined" size="large" onClick={() => startSwap(data.token0.id, data.token1.id)}>
                Swap
              </Button>
            ) : null}
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate(`/liquidity/add/${data.token0.id}/${data.token1.id}`)}
            >
              Add liquidity
            </Button>
          </Box>
        ) : null}
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
        {data
          ? rates.map((text) => (
              <Typography
                key={text}
                sx={{
                  ...amountType,
                  fontVariantNumeric: "proportional-nums",
                  fontSize: "0.9375rem",
                  px: 1.5,
                  py: 0.75,
                  borderRadius: "10px",
                  bgcolor: "background.light",
                }}
              >
                {text}
              </Typography>
            ))
          : [0, 1].map((key) => <Skeleton key={key} width={200} height={36} />)}
      </Box>

      <StatsRow
        items={[
          { label: "TVL", value: data ? formatUSD(data.reserveUSD) : null },
          { label: "Total volume", value: data ? formatVolume(data.volumeUSD, data.txCount) : null },
          { label: "Transactions", value: data ? formatCount(data.txCount) : null },
        ]}
      />

      {data ? (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, columnGap: 3 }}>
          <Section title="Pooled tokens">
            <Card sx={{ px: 2.5, py: 0.5, "& > * + *": { borderTop: 1, borderColor: "divider" } }}>
              <PooledToken token={data.token0} amount={data.reserve0} />
              <PooledToken token={data.token1} amount={data.reserve1} />
            </Card>
          </Section>
          <Section title="Details">
            <Card sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
              <AddressRow label="Pool" address={data.id} />
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1.5 }}>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  LP token supply
                </Typography>
                <Typography variant="body2">{formatAmount(data.totalSupply, { maxDecimals: 4 })}</Typography>
              </Box>
            </Card>
          </Section>
        </Box>
      ) : null}
    </Box>
  );
};

export default PoolPage;
